import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBuilding,
  FaCamera,
  FaCheckCircle,
  FaEnvelope,
  FaSave,
  FaUser,
  FaUserEdit,
} from "react-icons/fa";

import API from "./config/API";

function UpdateProfile() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState("");
  const [name, setName] = useState("");
  const [emailId, setEmailId] = useState("");

  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState("");

  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "",
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      navigate("/", { replace: true });
      return;
    }

    try {
      const user = JSON.parse(storedUser);

      setUserId(user?.userId || "");
      setName(user?.userName || "");
      setEmailId(user?.emailId || "");

      if (user?.profileImage) {
        setPreviewImage(user.profileImage);
      }
    } catch (error) {
      console.error("Invalid user data:", error);

      localStorage.removeItem("user");
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const showToast = (message, type = "success") => {
    setToast({
      show: true,
      message,
      type,
    });

    setTimeout(() => {
      setToast({
        show: false,
        message: "",
        type: "",
      });
    }, 2800);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Please select a valid image file.", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Profile image must be less than 5 MB.", "error");
      return;
    }

    setProfileImage(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      showToast("Please enter your full name.", "error");
      return;
    }

    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      showToast("Session expired. Please login again.", "error");

      setTimeout(() => {
        navigate("/", { replace: true });
      }, 800);

      return;
    }

    const user = JSON.parse(storedUser);

    const formData = new FormData();

    formData.append("userId", userId);
    formData.append("userName", name.trim());

    if (profileImage) {
      formData.append("profileImage", profileImage);
    }

    try {
      setLoading(true);

      const response = await fetch(`${API.V1}/pgOwner/updateProfile`, {
        method: "PUT",

        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
          Accept: "application/json",
        },

        body: formData,
      });

      // =====================================================
      // 401 SESSION EXPIRED
      // =====================================================

      if (response.status === 401) {
        localStorage.removeItem("user");

        showToast("Session expired. Please login again.", "error");

        setTimeout(() => {
          navigate("/", {
            replace: true,
          });
        }, 800);

        return;
      }

      const result = await response.json();

      if (response.ok && result?.statusCode === 200) {
        const updatedName = result?.data?.userName || name.trim();

        const updatedProfileImage = result?.data?.profileImage || previewImage;

        setName(updatedName);

        if (result?.data?.profileImage) {
          setPreviewImage(result.data.profileImage);
        }

        // Update localStorage
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

        const updatedUser = {
          ...currentUser,
          userName: updatedName,
          profileImage: updatedProfileImage,
        };

        localStorage.setItem("user", JSON.stringify(updatedUser));

        setProfileImage(null);

        showToast(
          result?.message || "Profile updated successfully.",
          "success",
        );
      } else {
        showToast(result?.message || "Profile update failed.", "error");
      }
    } catch (error) {
      console.error("Update profile error:", error);

      showToast("Unable to update profile. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePgData = () => {
    navigate("/updatePgOwnerData");
  };

  const defaultProfileImage =
    "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  return (
    <div style={styles.page}>
      {/* =====================================================
          BACKGROUND DECORATION
      ====================================================== */}

      <div style={styles.backgroundCircleOne}></div>
      <div style={styles.backgroundCircleTwo}></div>

      {/* =====================================================
          TOAST
      ====================================================== */}

      {toast.show && (
        <div
          style={{
            ...styles.toast,
            background:
              toast.type === "success"
                ? "linear-gradient(135deg,#059669,#10b981)"
                : "linear-gradient(135deg,#dc2626,#ef4444)",
          }}
        >
          <div style={styles.toastIcon}>
            {toast.type === "success" ? <FaCheckCircle /> : "!"}
          </div>

          <span>{toast.message}</span>
        </div>
      )}

      {/* =====================================================
          MAIN CARD
      ====================================================== */}

      <div style={styles.card}>
        {/* ===================================================
            HEADER
        ==================================================== */}

        <div style={styles.header}>
          <button
            type="button"
            style={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
            <span>Back</span>
          </button>

          <div style={styles.headerCenter}>
            <div style={styles.headerIcon}>
              <FaUserEdit />
            </div>

            <div>
              <h1 style={styles.title}>Update Profile</h1>

              <p style={styles.subtitle}>
                Manage your personal account details
              </p>
            </div>
          </div>

          <div style={styles.headerSpacer}></div>
        </div>

        {/* ===================================================
            PROFILE SUMMARY
        ==================================================== */}

        <div style={styles.profileSummary}>
          <div style={styles.profileImageWrapper}>
            <img
              src={previewImage || defaultProfileImage}
              alt="Profile"
              style={styles.profileImage}
            />

            <label
              htmlFor="profileImage"
              style={styles.cameraButton}
              title="Change profile image"
            >
              <FaCamera size={15} />
            </label>
          </div>

          <div style={styles.profileSummaryText}>
            <h2 style={styles.profileName}>{name || "Your Name"}</h2>

            <div style={styles.profileEmail}>
              <FaEnvelope size={13} />
              <span>{emailId || "Email address"}</span>
            </div>

            <div style={styles.profileBadge}>
              <FaUser size={11} />
              PG Owner Account
            </div>
          </div>
        </div>

        {/* ===================================================
            FORM
        ==================================================== */}

        <form onSubmit={handleSubmit}>
          {/* IMAGE INPUT */}

          <div style={styles.imageUploadSection}>
            <label htmlFor="profileImage" style={styles.uploadLabel}>
              Change profile picture
            </label>

            <input
              id="profileImage"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleImageChange}
              style={styles.fileInput}
            />

            <span style={styles.fileHint}>JPG, PNG or WEBP • Maximum 5 MB</span>
          </div>

          {/* =================================================
              FULL NAME
          ================================================== */}

          <div style={styles.formGroup}>
            <label style={styles.label}>Full Name</label>

            <div style={styles.inputWrapper}>
              <FaUser style={styles.inputIcon} />

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={styles.input}
                placeholder="Enter your full name"
                maxLength={100}
              />
            </div>
          </div>

          {/* =================================================
              EMAIL
          ================================================== */}

          <div style={styles.formGroup}>
            <label style={styles.label}>Email Address</label>

            <div
              style={{
                ...styles.inputWrapper,
                background: "#f1f5f9",
              }}
            >
              <FaEnvelope
                style={{
                  ...styles.inputIcon,
                  color: "#94a3b8",
                }}
              />

              <input
                type="email"
                value={emailId}
                disabled
                style={{
                  ...styles.input,
                  background: "#f1f5f9",
                  color: "#64748b",
                  cursor: "not-allowed",
                }}
              />
            </div>

            <span style={styles.helperText}>
              Email address cannot be changed from this page.
            </span>
          </div>

          {/* =================================================
              BUTTONS
          ================================================== */}

          <div style={styles.buttonContainer}>
            {/* BACK */}

            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft />
              Back
            </button>

            {/* PG PROFILE */}

            {/* <button
              type="button"
              style={styles.pgProfileButton}
              onClick={handleUpdatePgData}
            >
              <FaBuilding />
              <span>Update PG Data</span>
            </button> */}

            {/* PERSONAL PROFILE */}

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.saveButton,
                ...(loading ? styles.saveButtonDisabled : {}),
              }}
            >
              {loading ? (
                <>
                  <span style={styles.spinner}></span>
                  Updating...
                </>
              ) : (
                <>
                  <FaSave />
                  Update Profile
                </>
              )}
            </button>
          </div>
        </form>

        {/* ===================================================
            PG PROFILE INFORMATION CARD
        ==================================================== */}

        <div style={styles.pgInfoCard}>
          <div style={styles.pgInfoIcon}>
            <FaBuilding />
          </div>

          <div style={styles.pgInfoContent}>
            <h3 style={styles.pgInfoTitle}>Manage PG Information</h3>

            <p style={styles.pgInfoText}>
              Update your PG name, type, location, contact details, address and
              other registration information.
            </p>
          </div>

          <button
            type="button"
            style={styles.pgInfoButton}
            onClick={handleUpdatePgData}
          >
            <FaBuilding />
            Edit PG
            <span>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default UpdateProfile;

/* =========================================================
   MODERN STYLES
========================================================= */

const styles = {
  /* =======================================================
     PAGE
  ======================================================== */

  page: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "35px 20px",
    boxSizing: "border-box",
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(135deg,#eff6ff 0%,#f8fafc 45%,#eef2ff 100%)",
    fontFamily: '"Inter","Segoe UI",Arial,sans-serif',
  },

  backgroundCircleOne: {
    position: "absolute",
    width: "420px",
    height: "420px",
    borderRadius: "50%",
    background: "radial-gradient(circle,#bfdbfe 0%,rgba(191,219,254,0) 70%)",
    top: "-180px",
    right: "-120px",
    pointerEvents: "none",
  },

  backgroundCircleTwo: {
    position: "absolute",
    width: "500px",
    height: "500px",
    borderRadius: "50%",
    background: "radial-gradient(circle,#ddd6fe 0%,rgba(221,214,254,0) 70%)",
    bottom: "-250px",
    left: "-180px",
    pointerEvents: "none",
  },

  /* =======================================================
     CARD
  ======================================================== */

  card: {
    width: "100%",
    maxWidth: "760px",
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(226,232,240,0.9)",
    borderRadius: "28px",
    padding: "30px",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 2,
    boxShadow: "0 25px 70px rgba(15,23,42,.14)",
    backdropFilter: "blur(14px)",
  },

  /* =======================================================
     HEADER
  ======================================================== */

  header: {
    display: "grid",
    gridTemplateColumns: "100px 1fr 100px",
    alignItems: "center",
    borderBottom: "1px solid #e2e8f0",
    paddingBottom: "24px",
    marginBottom: "28px",
  },

  backButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    width: "fit-content",
    border: "none",
    background: "#f1f5f9",
    color: "#475569",
    padding: "10px 14px",
    borderRadius: "11px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "700",
  },

  headerCenter: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "14px",
  },

  headerIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "20px",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    boxShadow: "0 10px 25px rgba(37,99,235,.25)",
  },

  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "26px",
    fontWeight: "800",
    letterSpacing: "-.5px",
  },

  subtitle: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: "13px",
  },

  headerSpacer: {
    width: "100%",
  },

  /* =======================================================
     PROFILE SUMMARY
  ======================================================== */

  profileSummary: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "22px",
    borderRadius: "20px",
    background: "linear-gradient(135deg,#eff6ff,#f8fafc)",
    border: "1px solid #dbeafe",
    marginBottom: "26px",
  },

  profileImageWrapper: {
    width: "102px",
    height: "102px",
    borderRadius: "50%",
    position: "relative",
    flexShrink: 0,
  },

  profileImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
    border: "4px solid #ffffff",
    boxShadow: "0 10px 25px rgba(37,99,235,.2)",
    background: "#e2e8f0",
  },

  cameraButton: {
    position: "absolute",
    right: "-2px",
    bottom: "-2px",
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    color: "#fff",
    cursor: "pointer",
    border: "3px solid #fff",
    boxSizing: "border-box",
  },

  profileSummaryText: {
    minWidth: 0,
  },

  profileName: {
    margin: 0,
    color: "#0f172a",
    fontSize: "22px",
    fontWeight: "800",
  },

  profileEmail: {
    marginTop: "7px",
    display: "flex",
    alignItems: "center",
    gap: "7px",
    color: "#64748b",
    fontSize: "13px",
    wordBreak: "break-word",
  },

  profileBadge: {
    marginTop: "11px",
    width: "fit-content",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#dbeafe",
    color: "#1d4ed8",
    fontSize: "11px",
    fontWeight: "700",
  },

  /* =======================================================
     IMAGE UPLOAD
  ======================================================== */

  imageUploadSection: {
    marginBottom: "24px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px dashed #93c5fd",
    background: "#f8fbff",
  },

  uploadLabel: {
    display: "block",
    color: "#1e3a8a",
    fontSize: "14px",
    fontWeight: "700",
    marginBottom: "10px",
  },

  fileInput: {
    width: "100%",
    padding: "10px",
    boxSizing: "border-box",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    fontSize: "13px",
    cursor: "pointer",
  },

  fileHint: {
    display: "block",
    marginTop: "7px",
    color: "#94a3b8",
    fontSize: "11px",
  },

  /* =======================================================
     FORM
  ======================================================== */

  formGroup: {
    marginBottom: "22px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "700",
  },

  inputWrapper: {
    width: "100%",
    minHeight: "50px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxSizing: "border-box",
    padding: "0 15px",
    borderRadius: "13px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    transition: "all .2s ease",
  },

  inputIcon: {
    color: "#64748b",
    flexShrink: 0,
  },

  input: {
    width: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#0f172a",
    fontSize: "14px",
    fontWeight: "500",
    padding: "14px 0",
  },

  helperText: {
    display: "block",
    marginTop: "7px",
    color: "#94a3b8",
    fontSize: "11px",
  },

  /* =======================================================
     BUTTONS
  ======================================================== */

  buttonContainer: {
    display: "grid",
    gridTemplateColumns: "0.8fr 1.35fr 1.5fr",
    gap: "12px",
    marginTop: "30px",
  },

  secondaryButton: {
    minHeight: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    border: "1px solid #cbd5e1",
    borderRadius: "13px",
    background: "#f8fafc",
    color: "#475569",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "700",
  },

  pgProfileButton: {
    minHeight: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    border: "none",
    borderRadius: "13px",
    background: "linear-gradient(135deg,#0f766e,#14b8a6)",
    color: "#fff",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "700",
    boxShadow: "0 10px 25px rgba(20,184,166,.2)",
  },

  saveButton: {
    minHeight: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    border: "none",
    borderRadius: "13px",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    color: "#fff",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "700",
    boxShadow: "0 10px 25px rgba(37,99,235,.25)",
  },

  saveButtonDisabled: {
    background: "#94a3b8",
    cursor: "not-allowed",
    boxShadow: "none",
  },

  spinner: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,.4)",
    borderTopColor: "#ffffff",
    animation: "profileSpin 0.7s linear infinite",
  },

  /* =======================================================
     PG INFO CARD
  ======================================================== */

  pgInfoCard: {
    marginTop: "26px",
    padding: "18px",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    gap: "14px",
    background: "linear-gradient(135deg,#f0fdfa,#ecfeff)",
    border: "1px solid #99f6e4",
  },

  pgInfoIcon: {
    width: "46px",
    height: "46px",
    borderRadius: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#0f766e",
    background: "#ccfbf1",
  },

  pgInfoContent: {
    flex: 1,
    minWidth: 0,
  },

  pgInfoTitle: {
    margin: 0,
    color: "#134e4a",
    fontSize: "14px",
    fontWeight: "800",
  },

  pgInfoText: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.5,
  },

  pgInfoButton: {
    flexShrink: 0,
    border: "none",
    borderRadius: "10px",
    padding: "10px 14px",
    background: "#0f766e",
    color: "#fff",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    gap: "7px",
  },

  /* =======================================================
     TOAST
  ======================================================== */

  toast: {
    position: "fixed",
    top: "22px",
    right: "22px",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    maxWidth: "380px",
    padding: "13px 17px",
    borderRadius: "13px",
    color: "#fff",
    fontSize: "13px",
    fontWeight: "700",
    boxShadow: "0 15px 35px rgba(15,23,42,.22)",
  },

  toastIcon: {
    width: "22px",
    height: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    background: "rgba(255,255,255,.2)",
    flexShrink: 0,
  },
};

/* =========================================================
   RESPONSIVE STYLES
========================================================= */

const responsiveStyle = document.createElement("style");

responsiveStyle.innerHTML = `
  @keyframes profileSpin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 700px) {

    .profile-page-card {
      padding: 22px !important;
    }

  }

  @media (max-width: 600px) {

    body {
      overflow-x: hidden;
    }

    .profile-mobile-buttons {
      grid-template-columns: 1fr !important;
    }

  }
`;

if (
  typeof document !== "undefined" &&
  !document.getElementById("profile-modern-style")
) {
  responsiveStyle.id = "profile-modern-style";

  document.head.appendChild(responsiveStyle);
}
